import React from "react";
import Select, { MultiValue, ActionMeta, StylesConfig } from "react-select";
import makeAnimated from "react-select/animated";
import { useField, useFormikContext } from "formik";
import { Error } from "../Error";
import LabelForm from "components/RFQ/LabelForm/LabelForm";
import { createSelectStyles } from "../styles/selectStyles";
import { LabelValue } from "newui/types/LabelValue.type";

const animatedComponents = makeAnimated();

interface SectorsMultiProps {
	name: string;
	className?: string;
	isLoading?: boolean;
	label?: string;
	maxOptions?: number;
	noLabel?: boolean;
	options: LabelValue<number>[];
	placeholder?: string;
	required?: boolean;
	sortingValue?: boolean;
	testId?: string;
	value: LabelValue<number>[];
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
	customOnChange?: (
		option: MultiValue<LabelValue<number>>,
		actionMeta: ActionMeta<LabelValue<number>>
	) => void;
}

export const SectorsMulti: React.FC<SectorsMultiProps> = ({
	options,
	name,
	setFieldValue,
	value,
	noLabel = false,
	maxOptions,
	customOnChange,
	testId,
	label,
	required,
	className,
	placeholder,
	sortingValue = false,
	isLoading = false,
}) => {
	const [field, meta] = useField(name);
	const { setFieldTouched } = useFormikContext();

	const isError = Boolean(meta.error && meta.touched);
	// @ts-ignore
	const customStyles: StylesConfig<SelectOption, true> = createSelectStyles({
		isError,
		maxWidth: "none",
	});

	const handleOnChange = (
		options: MultiValue<LabelValue<number>>,
		actionMeta: ActionMeta<LabelValue<number>>
	): void => {
		// Create array of values for Formik
		const valuesArray = options ? options.map((option) => option.value) : [];
		const finalArray = sortingValue
			? valuesArray.sort((a, b) => a - b)
			: valuesArray;
		setFieldValue(name, finalArray);

		customOnChange && customOnChange(options, actionMeta);
	};

	const getNoOptionsMessage = (): string => {
		if (maxOptions && value.length === maxOptions) {
			return `Maximum of ${maxOptions} sectors allowed`;
		}
		return "No options available";
	};

	const getAvailableOptions = (): LabelValue<number>[] => {
		return maxOptions && value.length === maxOptions ? [] : options;
	};

	return (
		<div
			className={className}
			style={{ position: "relative" }}
		>
			{label && !noLabel && (
				// @ts-ignore
				<LabelForm
					label={label}
					required={required}
				/>
			)}

			<Select<LabelValue<number>, true>
				isMulti
				styles={customStyles}
				closeMenuOnSelect={false}
				components={{
					...animatedComponents,
					IndicatorSeparator: () => null,
				}}
				options={getAvailableOptions()}
				noOptionsMessage={getNoOptionsMessage}
				onChange={handleOnChange}
				onBlur={() => {
					setFieldTouched(name, true);
				}}
				value={value}
				placeholder={placeholder || "Select.."}
				className={name}
				data-testid={testId}
				isLoading={isLoading}
			/>
			{
				// @ts-ignore
				<Error meta={meta} />
			}
		</div>
	);
};
